import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { Platform } from '@ionic/angular';
import { LessonService, Lesson } from '../services/lesson.service';
import { ProgressService } from '../services/progress.service';

interface TypingMetrics {
  wpm: number;
  accuracy: number;
  errors: number;
  progress: number;
}

interface TamilKeyMap {
  [key: string]: string;
}

interface KeyboardKey {
  tamil: string;
  english: string;
  isHomeKey?: boolean;
  originalUyir?: string;
}

interface KeyboardLayout {
  top: KeyboardKey[];
  middle: KeyboardKey[];
  bottom: KeyboardKey[];
}

@Component({
  selector: 'app-typing',
  templateUrl: './typing.page.html',
  styleUrls: ['./typing.page.scss'],
})
export class TypingPage implements OnInit, OnDestroy {
  currentLesson: Lesson | null = null;
  userInput: string = '';
  targetText: string = '';
  currentPosition: number = 0;
  isLessonComplete: boolean = false;
  lessonStarted: boolean = false;
  userAttempt: string = '';
  romanTransliteration: string = '';
  hasCompleteWord: boolean = false;
  isPlaying: boolean = false;
  expectedKey: string = '';
  currentTarget: string = '';
  currentCharacterIndex: number = 0;
  currentExerciseIndex: number = 0;
  isCorrect: boolean = false;
  isIncorrect: boolean = false;
  typingFeedback: string = '';
  isMobilePlatform: boolean = false;
  isSystemTamilKeyboard: boolean = false;
  currentKey: string = '';
  currentLetterIndex: number = 0;
  incorrectKey: string | null = null;
  selectedMei: KeyboardKey | null = null;
  uyirSigns: string[] = ['', 'ா', 'ி', 'ீ', 'ு', 'ூ', 'ெ', 'ே', 'ை', 'ொ', 'ோ', 'ௌ'];

  @ViewChild('inputArea') inputArea!: ElementRef;

  metrics: TypingMetrics = {
    wpm: 0,
    accuracy: 0,
    errors: 0,
    progress: 0
  };
  
  private startTime?: number;
  private subscriptions: Subscription[] = [];
  private errorPositions: Set<number> = new Set();

  keyboardLayout: KeyboardLayout = {
    top: [
      { tamil: 'அ', english: 'q' },
      { tamil: 'ஆ', english: 'w' },
      { tamil: 'இ', english: 'e' },
      { tamil: 'ஈ', english: 'r' },
      { tamil: 'க்', english: 't' },
      { tamil: 'ச்', english: 'y' },
      { tamil: 'ட்', english: 'u' },
      { tamil: 'த்', english: 'i' },
      { tamil: 'ப்', english: 'o' },
      { tamil: 'ற்', english: 'p' }
    ],
    middle: [
      { tamil: 'உ', english: 'a' },
      { tamil: 'ஊ', english: 's' },
      { tamil: 'எ', english: 'd' },
      { tamil: 'ஏ', english: 'f', isHomeKey: true },
      { tamil: 'ங்', english: 'g' },
      { tamil: 'ஞ்', english: 'h' },
      { tamil: 'ண்', english: 'j', isHomeKey: true },
      { tamil: 'ந்', english: 'k' },
      { tamil: 'ம்', english: 'l' },
      { tamil: 'ன்', english: ';' }
    ],
    bottom: [
      { tamil: 'ஐ', english: 'z' },
      { tamil: 'ஒ', english: 'x' },
      { tamil: 'ஓ', english: 'c' },
      { tamil: 'ஔ', english: 'v' },
      { tamil: 'ய்', english: 'b' },
      { tamil: 'ர்', english: 'n' },
      { tamil: 'ல்', english: 'm' },
      { tamil: 'வ்', english: ',' },
      { tamil: 'ழ்', english: '.' },
      { tamil: 'ள்', english: '/' }
    ]
  };

  private englishToTamilMap: TamilKeyMap = {
    'q': 'அ', 'w': 'ஆ', 'e': 'இ', 'r': 'ஈ', 'a': 'உ', 's': 'ஊ', 
    'd': 'எ', 'f': 'ஏ', 'z': 'ஐ', 'x': 'ஒ', 'c': 'ஓ', 'v': 'ஔ',
    't': 'க்', 'y': 'ச்', 'u': 'ட்', 'i': 'த்', 'o': 'ப்', 'p': 'ற்',
    'g': 'ங்', 'h': 'ஞ்', 'j': 'ண்', 'k': 'ந்', 'l': 'ம்', ';': 'ன்',
    'b': 'ய்', 'n': 'ர்', 'm': 'ல்', ',': 'வ்', '.': 'ழ்', '/': 'ள்', '`': 'ஃ'
  };

  // Add new audio properties
  private correctSound: HTMLAudioElement | null;
  private wrongSound: HTMLAudioElement | null;

  // Add new property to control keyboard help visibility
  showManual: boolean = false;

  // Change from private to public
  public isWaitingForUyir: boolean = false;

  // Add this property to control content height
  contentStyle = {
    height: 'calc(100% - 56px)' // Adjust for header height
  };

  // Add new property to track the required Mei key
  requiredMeiKey: string | null = null;

  // Add new property for hand overlay toggle
  showHandOverlay: boolean = false;

  // Add this as a public property
  public get expectedChar(): string {
    return this.getExpectedCharacter();
  }

  windowWidth: number = window.innerWidth;

  constructor(
    private lessonService: LessonService,
    private progressService: ProgressService,
    public platform: Platform
  ) {
    this.isMobilePlatform = this.platform.is('ios') || this.platform.is('android');
    
    // Initialize audio elements
    this.correctSound = new Audio('assets/sounds/typing.mp3');
    this.wrongSound = new Audio('assets/sounds/wrong.mp3');
    
    // Preload the sounds
    this.correctSound.load();
    this.wrongSound.load();

    // Add this to adjust content height for mobile devices
    if (this.isMobilePlatform) {
      this.contentStyle.height = 'calc(100% - 56px - env(safe-area-inset-bottom))';
    }
  }

  ngOnInit() {
    this.loadLesson();
    
    // Load hand overlay preference
    const savedHandOverlay = localStorage.getItem('showHandOverlay');
    if (savedHandOverlay !== null) {
      this.showHandOverlay = savedHandOverlay === 'true';
    }
  }

  private loadLesson() {
    this.currentLesson = this.lessonService.getCurrentLesson();
    this.targetText = (this.currentLesson?.content || 'அ ஆ இ ஈ').replace(/\s+/g, '');
    this.resetLesson();
    
    // Reset keyboard state
    this.selectedMei = null;
    this.isWaitingForUyir = false;
    
    setTimeout(() => {
        const firstChar = this.getExpectedCharacter();
        // Check if first character requires Mei key
        const nextChar = this.targetText[1];
        if (this.isVowelMarker(nextChar)) {
            // If it's a UyirMei, set the Mei key as current
            const meiChar = this.targetText[0] + '்';
            this.currentKey = meiChar;
            this.requiredMeiKey = meiChar;
            // Find and set the selected Mei
            const meiKey = this.findMeiKey(meiChar);
            if (meiKey) {
                this.selectedMei = meiKey;
                this.isWaitingForUyir = true;
            }
        } else {
            this.currentKey = firstChar;
        }
    }, 100);
  }

  onInputChange(event: any) {
    const value = event.target.value;
    
    // For system keyboard input
    if (this.isMobilePlatform || this.isSystemTamilKeyboard) {
        const lastChar = value[value.length - 1];
        
        // Only process if there's a new character
        if (lastChar && value.length > this.userInput.length) {
            this.onKeyPress(lastChar);
        }
        
        // Keep the input field showing only the correct characters
        event.target.value = this.userInput;
    }
  }

  checkLessonProgress(input: string) {
    // Implement lesson progress checking logic
  }

  clear() {
    this.userAttempt = '';
    this.romanTransliteration = '';
    this.resetLesson();
  }

  replayWord() {
    if (this.hasCompleteWord) {
      // Implement word replay logic
    }
  }

  private startLesson() {
    this.lessonStarted = true;
    this.startTime = Date.now();
  }

  private handleKeyPress(key: string) {
    if (this.isLessonComplete) return;

    const expectedChar = this.getExpectedCharacter();
    const nextChar = this.targetText[this.currentPosition + 1];
    const isExpectedUyirMei = this.isVowelMarker(nextChar) || 
                             (this.targetText[this.currentPosition] === 'க' && !nextChar);
    
    console.log('=== Key Press Debug ===\n' + JSON.stringify({
        action: 'handleKeyPress',
        pressedKey: key,
        expectedChar: expectedChar,
        currentPosition: this.currentPosition,
        nextChar: nextChar,
        isExpectedUyirMei,
        isWaitingForUyir: this.isWaitingForUyir,
        selectedMei: this.selectedMei?.tamil,
        currentKey: this.currentKey,
        targetText: this.targetText.slice(this.currentPosition, this.currentPosition + 5)
    }, null, 2));
    
    // For UyirMei characters
    if (isExpectedUyirMei) {
        if (key.endsWith('்')) {
            // Only set waiting state if correct Mei is pressed
            if (key.slice(0, -1) === this.targetText[this.currentPosition]) {
                this.isWaitingForUyir = true;
                this.selectedMei = this.findMeiKey(key);
                this.currentKey = key; // Keep the Mei key highlighted
                return;
            }
        } else if (this.isWaitingForUyir && this.selectedMei) {
            const baseChar = this.selectedMei.tamil.slice(0, -1);
            const uyirChar = this.findUyirFromKey(key);
            
            if (uyirChar) {
                const uyirIndex = this.getUyirIndex(uyirChar);
                const combinedChar = baseChar + (uyirIndex === 0 ? '' : this.uyirSigns[uyirIndex]);
                this.currentKey = combinedChar; // Update to show the combined character
                this.validateAndUpdateProgress(combinedChar);
            }
            return;
        }
    }
    
    // For simple characters
    this.currentKey = key;
    this.validateAndUpdateProgress(key);
  }

  private validateAndUpdateProgress(inputChar: string) {
    const expectedChar = this.getExpectedCharacter();
    const nextChar = this.targetText[this.currentPosition + 1];
    const isExpectedUyirMei = this.isVowelMarker(nextChar) || 
                             (this.targetText[this.currentPosition] === 'க' && !nextChar);
    const nextCharIsPulli = nextChar === '்';
    
    console.log('=== Validation Debug ===\n' + JSON.stringify({
        action: 'validateAndUpdateProgress',
        inputChar: inputChar,
        expectedChar: expectedChar,
        currentPosition: this.currentPosition,
        isWaitingForUyir: this.isWaitingForUyir,
        selectedMei: this.selectedMei?.tamil,
        currentKey: this.currentKey,
        isExpectedUyirMei: isExpectedUyirMei,
        nextCharIsPulli: nextCharIsPulli,
        targetText: this.targetText.slice(this.currentPosition, this.currentPosition + 5)
    }, null, 2));
    
    // Handle Mei key press
    if (inputChar.endsWith('்')) {
        const meiBase = inputChar.slice(0, -1);
        
        // Case 1: Current character should be a Mei
        if (nextCharIsPulli) {
            if (inputChar === expectedChar) {
                this.handleCorrectInput();
            } else {
                this.handleIncorrectInput();
            }
            return;
        }
        
        // Case 2: Current character could be either simple or part of UyirMei
        if (meiBase === expectedChar || (isExpectedUyirMei && meiBase === this.targetText[this.currentPosition])) {
            this.isWaitingForUyir = true;
            this.selectedMei = this.findMeiKey(inputChar);
            return;
        }
        
        // Case 3: Current character should be a simple character
        if (!isExpectedUyirMei && !nextCharIsPulli) {
            this.handleIncorrectInput();
            return;
        }
        
        return;  // Return early for any other Mei input
    }
    
    // Handle UyirMei combinations and simple characters
    if (this.isWaitingForUyir) {
        if (inputChar === expectedChar || 
            (inputChar === this.selectedMei?.tamil.slice(0, -1) && expectedChar === inputChar)) {
            this.handleCorrectInput();
            this.isWaitingForUyir = false;
            this.selectedMei = null;
        }
        return;
    }
    
    // Handle regular characters
    if (inputChar === expectedChar) {
        this.handleCorrectInput();
    } else {
        this.handleIncorrectInput();
    }
  }

  private handleCorrectInput() {
    // Play sounds for correct input
    if (this.correctSound) {
        this.correctSound.currentTime = 0;
        this.correctSound.play().catch(err => console.log('Error playing correct sound:', err));
    }
    
    this.playAudioForChar(this.getExpectedCharacter());
    
    // Reset states
    this.isWaitingForUyir = false;
    this.selectedMei = null;
    
    // Update position based on character type
    if (this.targetText[this.currentPosition + 1] === '்') {
        this.currentPosition += 2;  // Move past both base char and pulli
    } else if (this.isVowelMarker(this.targetText[this.currentPosition + 1])) {
        this.currentPosition += 2;  // Move past both base char and vowel marker
    } else {
        this.currentPosition += 1;  // Move past single char
    }
    
    // Update logical position and letter index
    const logicalPosition = this.getLogicalPosition(this.currentPosition);
    this.currentLetterIndex = logicalPosition % 7;
    
    this.isCorrect = true;
    this.isIncorrect = false;
    
    setTimeout(() => {
        this.isCorrect = false;
    }, 200);
    
    this.updateMetrics();
    
    if (this.currentPosition >= this.targetText.length) {
        this.completeLesson();
    } else {
        // Update currentKey to next expected character
        this.currentKey = this.getExpectedCharacter();
    }
  }

  private handleIncorrectInput() {
    if (this.wrongSound) {
        this.wrongSound.currentTime = 0;
        this.wrongSound.play().catch(err => console.log('Error playing wrong sound:', err));
    }
    
    this.handleError();
    this.isCorrect = false;
    this.isIncorrect = true;
    
    // Store the incorrect key
    this.incorrectKey = this.currentKey;
    
    // Clear all states
    this.currentKey = '';
    this.isWaitingForUyir = false;
    this.selectedMei = null;
    
    // Reset states after animation
    setTimeout(() => {
        this.isIncorrect = false;
        this.incorrectKey = null;
        
        // Get the expected character and update states
        const expectedChar = this.getExpectedCharacter();
        if (expectedChar.endsWith('்')) {
            // If expecting a Mei character
            this.currentKey = expectedChar;
        } else if (this.isVowelMarker(this.targetText[this.currentPosition + 1])) {
            // If expecting a UyirMei combination
            const meiChar = expectedChar + '்';
            const meiKey = this.findMeiKey(meiChar);
            if (meiKey) {
                this.selectedMei = meiKey;
                this.isWaitingForUyir = true;
                this.currentKey = meiKey.tamil;
            }
        } else {
            // For simple characters
            this.currentKey = expectedChar;
        }
    }, 500); // Match this with CSS animation duration
  }

  private findMeiKey(meiChar: string): KeyboardKey | null {
    const allKeys = [
        ...this.keyboardLayout.top,
        ...this.keyboardLayout.middle,
        ...this.keyboardLayout.bottom
    ];
    return allKeys.find(k => k.tamil === meiChar) || null;
  }

  // Make getExpectedCharacter public
  public getExpectedCharacter(): string {
    if (this.currentPosition >= this.targetText.length) return '';
    
    const char = this.targetText[this.currentPosition];
    const nextChar = this.targetText[this.currentPosition + 1];
    
    // Check for UyirMei combinations
    if (this.isVowelMarker(nextChar)) {
        // If we haven't pressed the Mei key yet, show the Mei key first
        if (!this.isWaitingForUyir) {
            // Find the base Mei character needed
            const meiChar = char + '்';
            this.requiredMeiKey = meiChar;
            return meiChar;
        }
        // After Mei is pressed, show the full UyirMei
        return char + nextChar;
    }
    
    // For Mei characters (with pulli)
    if (nextChar === '்') {
        return char + nextChar;
    }
    
    // For simple characters
    this.requiredMeiKey = null;
    return char;
  }

  private isVowelMarker(char: string | undefined | null): boolean {
    // Consider implicit அ (no marker) as a vowel marker case
    if (!char) return false;
    if (char === '்') return false;
    
    // If next char is a consonant, and current char needs UyirMei combination
    if (this.isMeiChar(char)) {
        return true;
    }
    
    return Boolean(char && this.uyirSigns.includes(char));
  }

  // Add this helper method
  private isMeiChar(char: string): boolean {
    return char.endsWith('்');
  }

  private handleError() {
    this.metrics.errors++;
    this.errorPositions.add(this.currentPosition);
    this.updateMetrics();
  }

  private updateMetrics() {
    if (!this.startTime) return;

    const timeElapsed = (Date.now() - this.startTime) / 1000 / 60; // in minutes
    const wordsTypingd = this.currentPosition / 5; // assuming average word length of 5
    this.metrics.wpm = Math.round(wordsTypingd / timeElapsed);
    
    const totalAttempts = this.currentPosition + this.errorPositions.size;
    this.metrics.accuracy = Math.round((this.currentPosition / totalAttempts) * 100);
    
    this.metrics.progress = Math.round((this.currentPosition / this.targetText.length) * 100);
  }

  private completeLesson() {
    if (!this.currentLesson) return;
    
    this.isLessonComplete = true;
    this.progressService.saveLessonProgress({
      lessonId: this.currentLesson.id,
      wpm: this.metrics.wpm,
      accuracy: this.metrics.accuracy,
      errors: this.metrics.errors,
      completedAt: new Date()
    });
  }

  resetLesson() {
    // Reset all tracking variables
    this.userInput = '';
    this.currentPosition = 0;
    this.currentLetterIndex = 0;
    this.isLessonComplete = false;
    this.lessonStarted = false;
    this.startTime = undefined;
    this.errorPositions.clear();
    this.metrics = {
      wpm: 0,
      accuracy: 0,
      errors: 0,
      progress: 0
    };

    // Reset keyboard state
    this.selectedMei = null;
    this.isWaitingForUyir = false;
    this.isCorrect = false;
    this.isIncorrect = false;
    this.incorrectKey = null;

    // Reset current key to first character
    setTimeout(() => {
        this.currentKey = this.getExpectedCharacter();
    }, 100);

    // Focus on input area if it exists
    if (this.inputArea) {
        this.inputArea.nativeElement.focus();
    }
  }

  nextLesson() {
    if (this.lessonService.moveToNextLesson()) {
      this.loadLesson();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
    // Clean up audio elements
    if (this.correctSound) {
      this.correctSound.pause();
      this.correctSound = null;
    }
    if (this.wrongSound) {
      this.wrongSound.pause();
      this.wrongSound = null;
    }
  }

  private updateTransliteration() {
    if (!this.userAttempt) {
      this.romanTransliteration = '';
      return;
    }

    this.romanTransliteration = this.transliterateToRoman(this.userAttempt);
    
    // Check if we have a complete word
    const words = this.userAttempt.trim().split(/\s+/);
    const currentWord = words[words.length - 1];
    
    if (currentWord) {
      this.hasCompleteWord = this.isValidWord(currentWord);
      if (this.hasCompleteWord && !this.isPlaying) {
        this.playAudio(currentWord);
      }
    } else {
      this.hasCompleteWord = false;
    }
  }

  private transliterateToRoman(tamilText: string): string {
    // Tamil to Roman character mapping
    const tamilToRomanMap: { [key: string]: string } = {
      'அ': 'a', 'ஆ': 'ā', 'இ': 'i', 'ஈ': 'ī', 
      'உ': 'u', 'ஊ': 'ū', 'எ': 'e', 'ஏ': 'ē', 
      'ஐ': 'ai', 'ஒ': 'o', 'ஓ': 'ō', 'ஔ': 'au',
      'க்': 'k', 'ங்': 'ṅ', 'ச்': 'c', 'ஞ்': 'ñ',
      'ட்': 'ṭ', 'ண்': 'ṇ', 'த்': 't', 'ந்': 'n',
      'ப்': 'p', 'ம்': 'm', 'ய்': 'y', 'ர்': 'r',
      'ல்': 'l', 'வ்': 'v', 'ழ்': 'ḻ', 'ள்': 'ḷ',
      'ற்': 'ṟ', 'ன்': 'ṉ'
    };

    // Vowel marker mapping
    const vowelMarkerMap: { [key: string]: string } = {
      'ா': 'ā', 'ி': 'i', 'ீ': 'ī', 'ு': 'u', 'ூ': 'ū',
      'ெ': 'e', 'ே': 'ē', 'ை': 'ai', 'ொ': 'o', 'ோ': 'ō',
      'ௌ': 'au', '்': ''
    };

    let result = '';
    let i = 0;
    while (i < tamilText.length) {
      const char = tamilText[i];
      const nextChar = tamilText[i + 1];

      if (vowelMarkerMap[nextChar]) {
        // Handle consonant + vowel marker combinations
        const baseChar = tamilToRomanMap[char] || char;
        const vowelMark = vowelMarkerMap[nextChar];
        result += baseChar.replace(/a$/, '') + vowelMark;
        i += 2;
      } else {
        // Handle single characters
        result += tamilToRomanMap[char] || char;
        i++;
      }
    }

    return result;
  }

  private isValidWord(word: string): boolean {
    // Add your word validation logic here
    // For now, let's consider any non-empty string as valid
    return word.trim().length > 0;
  }

  private async playAudio(text: string) {
    if (!text || this.isPlaying) return;

    try {
      this.isPlaying = true;
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'ta-IN';
      utterance.rate = 0.8;

      utterance.onend = () => {
        this.isPlaying = false;
      };

      utterance.onerror = () => {
        this.isPlaying = false;
      };

      window.speechSynthesis.speak(utterance);
    } catch (error) {
      console.error('Error playing audio:', error);
      this.isPlaying = false;
    }
  }

  public hasError(index: number): boolean {
    return this.errorPositions.has(index);
  }

  onKeyPress(key: string) {
    if (this.isLessonComplete) return;

    if (!this.lessonStarted) {
      this.startLesson();
    }

    // Clear Mei selection if an Uyir key is pressed
    if (!key.endsWith('்')) {
      this.selectedMei = null;
    }

    this.handleKeyPress(key);
  }

  getDisplayCharacters() {
    const displayChars: { char: string, index: number }[] = [];
    let i = 0;
    
    while (i < this.targetText.length) {
      if (this.targetText[i + 1] === '்') {
        // Combine character with pulli
        displayChars.push({
          char: this.targetText[i] + this.targetText[i + 1],
          index: i
        });
        i += 2; // Skip pulli character
      } else {
        // Regular character
        displayChars.push({
          char: this.targetText[i],
          index: i
        });
        i++;
      }
    }
    
    return displayChars;
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // If lesson is complete, any key press will move to next lesson
    if (this.isLessonComplete) {
        event.preventDefault();
        this.nextLesson();
        return;
    }

    // Handle special keys
    if (event.key === 'Backspace') {
        event.preventDefault();
        this.handleBackspace();
        return;
    }

    // Handle regular keys
    if (event.key.length === 1) {
        event.preventDefault();
        const key = event.key.toLowerCase();
        
        console.log('=== Keyboard Event Debug ===\n' + JSON.stringify({
            action: 'handleKeyboardEvent',
            key: key,
            selectedMei: this.selectedMei?.tamil,
            currentKey: this.currentKey,
            isWaitingForUyir: this.isWaitingForUyir,
            englishToTamilMap: this.englishToTamilMap[key]
        }, null, 2));

        // Start the lesson if it hasn't started yet
        if (!this.lessonStarted) {
            this.startLesson();
        }
        
        // Check if it's a Mei key
        const allKeys = [
            ...this.keyboardLayout.top,
            ...this.keyboardLayout.middle,
            ...this.keyboardLayout.bottom
        ];
        
        const meiKey = allKeys.find((k: KeyboardKey) => 
            k.english === key && k.tamil.endsWith('்')
        );
            
        if (meiKey) {
            // Handle Mei key press - set as selected Mei
            this.selectedMei = meiKey;
            this.handleKeyPress(meiKey.tamil);
            this.updateMetrics();  // Add metrics update
        } else if (this.selectedMei) {
            // If a Mei is selected, try to form UyirMei combination
            const uyirKey = allKeys.find((k: KeyboardKey) => 
                k.english === key && !k.tamil.endsWith('்')
            );
                
            if (uyirKey) {
                const baseChar = this.selectedMei.tamil.slice(0, -1);
                const uyirIndex = ['அ', 'ஆ', 'இ', 'ஈ', 'உ', 'ஊ', 'எ', 'ஏ', 'ஐ', 'ஒ', 'ஓ', 'ஔ']
                    .indexOf(uyirKey.tamil);
                    
                if (uyirIndex !== -1) {
                    const combinedChar = baseChar + (uyirIndex === 0 ? '' : this.uyirSigns[uyirIndex]);
                    this.handleKeyPress(combinedChar);
                    this.updateMetrics();  // Add metrics update
                }
                this.selectedMei = null;
            }
        } else if (this.englishToTamilMap[key]) {
            if (!this.lessonStarted) {
                this.startLesson();
            }
            this.handleKeyPress(this.englishToTamilMap[key]);
            this.updateMetrics();  // Add metrics update
        } else {
            this.handleError();
            this.isCorrect = false;
            this.isIncorrect = true;
            this.incorrectKey = key;
            this.updateMetrics();  // Add metrics update
            
            setTimeout(() => {
                this.isIncorrect = false;
                this.incorrectKey = null;
            }, 200);
        }
    }
  }

  private handleBackspace() {
    if (this.currentPosition > 0) {
        // If previous character was a Mei with Pulli or UyirMei, move back 2 positions
        if (this.currentPosition >= 2 && 
            (this.targetText[this.currentPosition - 1] === '்' || 
             this.isVowelMarker(this.targetText[this.currentPosition - 1]))) {
            this.currentPosition -= 2;
        } else {
            this.currentPosition--;
        }
        
        // Update currentKey after backspace
        this.currentKey = this.getExpectedCharacter();
        this.updateMetrics();
    }
  }

  private playAudioForChar(char: string) {
    try {
        const audio = new Audio(`assets/audio/${char}.mp3`);
        audio.play().catch(error => console.error('Error playing letter audio:', error));
    } catch (error) {
        console.error('Error creating letter audio:', error);
    }
  }

  getNextCharacters(): string[] {
    const nextChars: string[] = [];
    const maxCharsToShow = 6;
    
    // Calculate start position considering Mei characters
    let startPos = this.currentPosition;
    let charsBack = 3;
    let tempPos = this.currentPosition;
    
    // Move back considering Mei and UyirMei combinations
    while (charsBack > 0 && tempPos > 0) {
      if (tempPos >= 2 && 
          (this.targetText[tempPos - 1] === '்' || this.isVowelMarker(this.targetText[tempPos - 1]))) {
        tempPos -= 2;
      } else {
        tempPos -= 1;
      }
      charsBack--;
    }
    startPos = tempPos;
    
    // Start collecting characters from start position
    let pos = startPos;
    
    while (nextChars.length < maxCharsToShow && pos < this.targetText.length) {
      let char = this.targetText[pos];
      const nextChar = this.targetText[pos + 1];
      
      if (nextChar === '்') {
        // Handle Mei character with pulli
        nextChars.push(char + nextChar);
        pos += 2;
      } else if (this.isVowelMarker(nextChar)) {
        // Handle UyirMei combination
        nextChars.push(char + nextChar);
        pos += 2;
      } else {
        // Handle single character
        nextChars.push(char);
        pos += 1;
      }
    }
    
    // Calculate the current letter index based on logical position
    let currentIndex = 0;
    let tempCurrentPos = startPos;
    
    while (tempCurrentPos < this.currentPosition) {
      if (tempCurrentPos + 1 < this.targetText.length && 
          (this.targetText[tempCurrentPos + 1] === '்' || 
           this.isVowelMarker(this.targetText[tempCurrentPos + 1]))) {
        tempCurrentPos += 2;
      } else {
        tempCurrentPos += 1;
      }
      currentIndex++;
    }
    
    this.currentLetterIndex = currentIndex;
    
    return nextChars;
  }

  // Update getLogicalPosition to handle both Mei and UyirMei combinations
  private getLogicalPosition(position: number): number {
    let logicalPos = 0;
    let pos = 0;
    
    while (pos < position && pos < this.targetText.length) {
        // Check for Mei character (with pulli) first
        if (this.targetText[pos + 1] === '்') {
            pos += 2;  // Skip base char and pulli
        } else if (this.isVowelMarker(this.targetText[pos + 1])) {
            pos += 2;  // Skip base char and vowel marker
        } else {
            pos += 1;  // Skip single char
        }
        logicalPos++;
    }
    
    return logicalPos;
  }

  // Add a method to handle sound volume
  setVolume(volume: number) {
    if (this.correctSound) {
      this.correctSound.volume = volume;
    }
    if (this.wrongSound) {
      this.wrongSound.volume = volume;
    }
  }

  showKeyboardHelp() {
    this.showManual = !this.showManual;
  }

  closeManual() {
    this.showManual = false;
  }

  getUyirKeys(row: 'top' | 'middle' | 'bottom'): KeyboardKey[] {
    if (this.selectedMei) {
        // Generate UyirMei combinations for the selected Mei
        const baseChar = this.selectedMei.tamil.slice(0, -1); // Remove pulli
        
        // Map each row to its corresponding Uyir signs
        const rowMappings = {
            'top': [
                ['', 'ா', 'ி', 'ீ'],  // அ, ஆ, இ, ஈ
                [0, 1, 2, 3]  // indices for reference
            ],
            'middle': [
                ['ு', 'ூ', 'ெ', 'ே'],  // உ, ஊ, எ, ஏ
                [4, 5, 6, 7]  // indices for reference
            ],
            'bottom': [
                ['ை', 'ொ', 'ோ', 'ௌ'],  // ஐ, ஒ, ஓ, ஔ
                [8, 9, 10, 11]  // indices for reference
            ]
        };

        const [signs, indices] = rowMappings[row];
        
        return this.keyboardLayout[row].slice(0, 4).map((key, i) => ({
            tamil: baseChar + signs[i],
            english: key.english,
            originalUyir: key.tamil
        }));
    }
    
    // Return regular Uyir keys if no Mei is selected
    return this.keyboardLayout[row].slice(0, 4);
  }

  onMeiKeyPress(key: KeyboardKey) {
    if (this.selectedMei?.tamil === key.tamil) {
      // If same Mei key is pressed again, clear selection
      this.selectedMei = null;
    } else {
      // Select new Mei key
      this.selectedMei = key;
    }
    
    // Also handle the key press
    this.onKeyPress(key.tamil);
  }

  // Update combineUyirMei to handle vowel markers correctly
  combineUyirMei(char: string): string {
    // If already a combined form, return as is
    if (char.length > 1) {
      return char;
    }
    
    // For single characters, return as is
    return char;
  }

  // Add this helper method to get the Uyir index
  private getUyirIndex(uyirChar: string): number {
    const uyirOrder = ['அ', 'ஆ', 'இ', 'ஈ', 'உ', 'ஊ', 'எ', 'ஏ', 'ஐ', 'ஒ', 'ஓ', 'ஔ'];
    return uyirOrder.indexOf(uyirChar);
  }

  // Add this helper method to find the Uyir character from a key
  private findUyirFromKey(key: string): string | null {
    // First check if it's a direct Uyir key
    const allKeys = [
        ...this.keyboardLayout.top,
        ...this.keyboardLayout.middle,
        ...this.keyboardLayout.bottom
    ];
    
    const uyirKey = allKeys.find((k: KeyboardKey) => 
        k.tamil === key && !k.tamil.endsWith('்')
    );
    
    if (uyirKey) {
        return uyirKey.tamil;
    }
    
    // If not found, check if it's already a UyirMei and extract the Uyir part
    const uyirOrder = ['அ', 'ஆ', 'இ', 'ஈ', 'உ', 'ஊ', 'எ', 'ஏ', 'ஐ', 'ஒ', 'ஓ', 'ஔ'];
    for (let i = 0; i < uyirOrder.length; i++) {
        if (key === this.selectedMei?.tamil.slice(0, -1) + this.uyirSigns[i]) {
            return uyirOrder[i];
        }
    }
    
    return null;
  }

  // Add new method to toggle hand overlay
  toggleHandOverlay() {
    this.showHandOverlay = !this.showHandOverlay;
    // Optionally save preference to localStorage
    localStorage.setItem('showHandOverlay', String(this.showHandOverlay));
  }

  public isCorrectUyirForMei(uyirChar: string): boolean {
    if (!this.isWaitingForUyir || !this.selectedMei) {
        return false;
    }

    const expectedChar = this.getExpectedCharacter();
    const baseChar = this.selectedMei.tamil.slice(0, -1);
    
    // Map Uyir keys to their corresponding vowel markers
    const uyirToMarkerMap: { [key: string]: string } = {
        // Top row
        'அ': '',
        'ஆ': 'ா',
        'இ': 'ி',
        'ஈ': 'ீ',
        // Middle row
        'உ': 'ு',
        'ஊ': 'ூ',
        'எ': 'ெ',
        'ஏ': 'ே',
        // Bottom row
        'ஐ': 'ை',
        'ஒ': 'ொ',
        'ஓ': 'ோ',
        'ஔ': 'ௌ'
    };

    // Get the vowel marker for the current Uyir key
    const marker = uyirToMarkerMap[uyirChar];
    if (marker === undefined) return false;

    // Compare the expected character with what would be formed
    const wouldForm = baseChar + marker;
    const isMatch = wouldForm === expectedChar;
    
    console.log('=== isCorrectUyirForMei Debug ===\n' + JSON.stringify({
        uyirChar,
        expectedChar,
        baseChar,
        marker,
        wouldForm,
        isMatch
    }, null, 2));
    
    return isMatch;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.windowWidth = event.target.innerWidth;
  }
}